import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'

/**
 * WorkflowSection の表示順 (単日 → 週間 → 月間 → 年間) と、
 * 画面別の i18n key を 1 箇所に集約した正本。 backend valueobject.WorkflowSection.SortRank と
 * 順序を一致させる。
 *
 * option 列挙 (フィルタ / 編集フォーム) の全箇所がこの配列から導出するため、
 * 旧当日系の 3 値は読取互換のため enum に残すが、画面ではすべて単日へ正規化する。
 *
 * i18n key は admin / cases で namespace が違うため leaf 共通化せず両方を完全な静的リテラルで持つ
 * (= check-i18n-keys.sh は静的な文字列リテラルの完全キーのみ検証するので、 leaf からの動的生成に
 *  すると断面ラベルの key 存在チェックが効かなくなる)。
 * UNSPECIFIED は「—」表示なのでこの一覧には含めない (= 各画面が個別に fallback する)。
 *
 */
type WorkflowSectionItem = {
  value: WorkflowSection
  adminLabelKey: string
  casesLabelKey: string
}

export const WORKFLOW_SECTION_ITEMS: readonly WorkflowSectionItem[] = [
  {
    value: WorkflowSection.SINGLE_DAY,
    adminLabelKey: 'page:adminWorkflowTemplates.section.singleDay',
    casesLabelKey: 'page:workflowCases.section.singleDay',
  },
  {
    value: WorkflowSection.WEEKLY,
    adminLabelKey: 'page:adminWorkflowTemplates.section.weekly',
    casesLabelKey: 'page:workflowCases.section.weekly',
  },
  {
    value: WorkflowSection.MONTHLY,
    adminLabelKey: 'page:adminWorkflowTemplates.section.monthly',
    casesLabelKey: 'page:workflowCases.section.monthly',
  },
  {
    value: WorkflowSection.ANNUAL,
    adminLabelKey: 'page:adminWorkflowTemplates.section.annual',
    casesLabelKey: 'page:workflowCases.section.annual',
  },
]

export type WorkflowSectionLabelPage =
  | 'adminWorkflowTemplates'
  | 'workflowCases'

/**
 * section → i18n key を解決する。 表示一覧に無い section (UNSPECIFIED / 未知値) は undefined を返し、
 * caller 側で「—」等の fallback に落とす。
 */
export function workflowSectionLabelKey(
  page: WorkflowSectionLabelPage,
  section: WorkflowSection,
): string | undefined {
  const item = WORKFLOW_SECTION_ITEMS.find(
    (s) => s.value === canonicalWorkflowSection(section),
  )
  if (item === undefined) {
    return undefined
  }
  switch (page) {
    case 'adminWorkflowTemplates':
      return item.adminLabelKey
    case 'workflowCases':
      return item.casesLabelKey
  }
}
/** 旧当日系の保存値を、画面で扱う単日の正規値へ寄せる。 */
export function canonicalWorkflowSection(
  section: WorkflowSection,
): WorkflowSection {
  switch (section) {
    case WorkflowSection.TODAY:
    case WorkflowSection.TOMORROW:
    case WorkflowSection.DAY_AFTER_TOMORROW:
      return WorkflowSection.SINGLE_DAY
    default:
      return section
  }
}
